import { Injectable } from '@angular/core';
import { Selector, createSelector } from '@ngxs/store';
import { IciFlags } from 'src/generated/signalR/ici-flags';

@Injectable()
export class IciHelperSelector {
   @Selector()
   private static getIsIci() {
      return (iciId: number, ici: IciFlags) => {
         return (iciId & ici) > 0;
      };
   }

   public static getIsIciByFlag(iciFlag: IciFlags) {
      return createSelector([IciHelperSelector.getIsIci], (getIsIci: (iciId: number, ici: IciFlags) => number) => {
         return (iciId: number) => {
            return getIsIci(iciId, iciFlag);
         };
      });
   }

   // Additional ICIs
   @Selector([IciHelperSelector.getIsIciByFlag(IciFlags.INBOUND)])
   private static getIsInbound(getIsInbound: (iciId: number) => boolean) {
      return (iciId: number) => {
         return getIsInbound(iciId);
      };
   }

   @Selector([IciHelperSelector.getIsIciByFlag(IciFlags.INTERNAL)])
   private static getIsInternal(getIsInternal: (iciId: number) => boolean) {
      return (iciId: number) => {
         return getIsInternal(iciId);
      };
   }

   @Selector([IciHelperSelector.getIsIciByFlag(IciFlags.EXTERNAL)])
   private static getIsExternal(getIsExternal: (iciId: number) => boolean) {
      return (iciId: number) => {
         return getIsExternal(iciId);
      };
   }

   @Selector([IciHelperSelector.getIsIciByFlag(IciFlags.FALLBACK)])
   public static getIsFallBack(getIsFallBack: (iciId: number) => boolean) {
      return (iciId: number) => {
         return getIsFallBack(iciId);
      };
   }

   @Selector([IciHelperSelector.getIsIciByFlag(IciFlags.BUSY)])
   private static getIsBusy(getIsBusy: (iciId: number) => boolean) {
      return (iciId: number) => {
         return getIsBusy(iciId);
      };
   }

   @Selector([IciHelperSelector.getIsIciByFlag(IciFlags.NO_ANSWER)])
   private static getIsNoAnswer(getIsNoAnswer: (iciId: number) => boolean) {
      return (iciId: number) => {
         return getIsNoAnswer(iciId);
      };
   }

   @Selector([IciHelperSelector.getIsIciByFlag(IciFlags.FORWARDED)])
   private static getIsForwarded(getIsForwarded: (iciId: number) => boolean) {
      return (iciId: number) => {
         return getIsForwarded(iciId);
      };
   }

   @Selector([IciHelperSelector.getIsIciByFlag(IciFlags.TRANSFERRED)])
   private static getIsRedirected(getIsRedirected: (iciId: number) => boolean) {
      return (iciId: number) => {
         return getIsRedirected(iciId);
      };
   }

   // ICI_INBOUND + ICI_INTERNAL
   @Selector([IciHelperSelector.getIsInbound, IciHelperSelector.getIsInternal])
   public static getIsInboundInternal(getIsInbound: (iciId: number) => boolean, getIsInternal: (iciId: number) => boolean) {
      return (iciId: number) => {
         return getIsInbound(iciId) && getIsInternal(iciId);
      };
   }

   // ICI_INBOUND + ICI_INTERNAL + ICI_BUSY
   @Selector([IciHelperSelector.getIsInboundInternal, IciHelperSelector.getIsBusy])
   public static getIsInboundInternalBusy(getIsInboundInternal: (iciId: number) => boolean, getIsBusy: (iciId: number) => boolean) {
      return (iciId: number) => {
         return getIsInboundInternal(iciId) && getIsBusy(iciId);
      };
   }

   // ICI_INBOUND + ICI_INTERNAL + ICI_NO_ANSWER
   @Selector([IciHelperSelector.getIsInboundInternal, IciHelperSelector.getIsNoAnswer])
   public static getIsInboundInternalIsNoAnswer(getIsInboundInternal: (iciId: number) => boolean, getIsNoAnswer: (iciId: number) => boolean) {
      return (iciId: number) => {
         return getIsInboundInternal(iciId) && getIsNoAnswer(iciId);
      };
   }

   // ICI_INBOUND + ICI_INTERNAL + ICI_FORWARDED
   @Selector([IciHelperSelector.getIsInboundInternal, IciHelperSelector.getIsForwarded])
   public static getIsInboundInternalIsForwarded(getIsInboundInternal: (iciId: number) => boolean, getIsForwarded: (iciId: number) => boolean) {
      return (iciId: number) => {
         return getIsInboundInternal(iciId) && getIsForwarded(iciId);
      };
   }

   // ICI_INBOUND + ICI_INTERNAL + ICI_REDIRECTED
   @Selector([IciHelperSelector.getIsInboundInternal, IciHelperSelector.getIsRedirected])
   public static getIsInboundInternalIsRedirected(getIsInboundInternal: (iciId: number) => boolean, getIsRedirected: (iciId: number) => boolean) {
      return (iciId: number) => {
         return getIsInboundInternal(iciId) && getIsRedirected(iciId);
      };
   }

   // ICI_INBOUND + ICI_EXTERNAL
   @Selector([IciHelperSelector.getIsInbound, IciHelperSelector.getIsExternal])
   public static getIsInboundExternal(getIsInbound: (iciId: number) => boolean, getIsExternal: (iciId: number) => boolean) {
      return (iciId: number) => {
         return getIsInbound(iciId) && getIsExternal(iciId);
      };
   }

   // ICI_INBOUND + ICI_EXTERNAL + ICI_BUSY
   @Selector([IciHelperSelector.getIsInboundExternal, IciHelperSelector.getIsBusy])
   public static getIsInboundExternalBusy(getIsInboundExternal: (iciId: number) => boolean, getIsBusy: (iciId: number) => boolean) {
      return (iciId: number) => {
         return getIsInboundExternal(iciId) && getIsBusy(iciId);
      };
   }

   // ICI_INBOUND + ICI_EXTERNAL + ICI_NO_ANSWER
   @Selector([IciHelperSelector.getIsInboundExternal, IciHelperSelector.getIsNoAnswer])
   public static getIsInboundExternalIsNoAnswer(getIsInboundExternal: (iciId: number) => boolean, getIsNoAnswer: (iciId: number) => boolean) {
      return (iciId: number) => {
         return getIsInboundExternal(iciId) && getIsNoAnswer(iciId);
      };
   }

   // ICI_INBOUND + ICI_EXTERNAL + ICI_FORWARDED
   @Selector([IciHelperSelector.getIsInboundExternal, IciHelperSelector.getIsForwarded])
   public static getIsInboundExternalIsForwarded(getIsInboundExternal: (iciId: number) => boolean, getIsForwarded: (iciId: number) => boolean) {
      return (iciId: number) => {
         return getIsInboundExternal(iciId) && getIsForwarded(iciId);
      };
   }

   // ICI_INBOUND + ICI_EXTERNAL + ICI_REDIRECTED
   @Selector([IciHelperSelector.getIsInboundExternal, IciHelperSelector.getIsRedirected])
   public static getIsInboundExternalIsRedirected(getIsInboundExternal: (iciId: number) => boolean, getIsRedirected: (iciId: number) => boolean) {
      return (iciId: number) => {
         return getIsInboundExternal(iciId) && getIsRedirected(iciId);
      };
   }

   // ICI_INBOUND + ICI_INTERNAL + ICI_FALLBACK
   @Selector([IciHelperSelector.getIsInboundInternal, IciHelperSelector.getIsFallBack])
   public static getIsInboundInternalFallback(getIsInboundInternal: (iciId: number) => boolean, getIsFallback: (iciId: number) => boolean) {
      return (iciId: number) => {
         return getIsInboundInternal(iciId) && getIsFallback(iciId);
      };
   }

   // ICI_INBOUND + ICI_INTERNAL + ICI_FALLBACK
   @Selector([IciHelperSelector.getIsInboundInternalFallback, IciHelperSelector.getIsBusy])
   public static getIsInboundInternalFallbackBusy(getIsInboundInternalFallback: (iciId: number) => boolean, getIsBusy: (iciId: number) => boolean) {
      return (iciId: number) => {
         return getIsInboundInternalFallback(iciId) && getIsBusy(iciId);
      };
   }

   // ICI_INBOUND + ICI_INTERNAL + ICI_FALLBACK + ICI_NO_ANSWER
   @Selector([IciHelperSelector.getIsInboundInternalFallback, IciHelperSelector.getIsNoAnswer])
   public static getIsInboundInternalFallbackIsNoAnswer(getIsInboundInternalFallback: (iciId: number) => boolean, getIsNoAnswer: (iciId: number) => boolean) {
      return (iciId: number) => {
         return getIsInboundInternalFallback(iciId) && getIsNoAnswer(iciId);
      };
   }

   // ICI_INBOUND + ICI_INTERNAL + ICI_FALLBACK + ICI_FORWARDED
   @Selector([IciHelperSelector.getIsInboundInternalFallback, IciHelperSelector.getIsForwarded])
   public static getIsInboundInternalFallbackIsForwarded(getIsInboundInternalFallback: (iciId: number) => boolean, getIsForwarded: (iciId: number) => boolean) {
      return (iciId: number) => {
         return getIsInboundInternalFallback(iciId) && getIsForwarded(iciId);
      };
   }

   // ICI_INBOUND + ICI_EXTERNAL + ICI_FALLBACK
   @Selector([IciHelperSelector.getIsInboundExternal, IciHelperSelector.getIsFallBack])
   public static getIsInboundExternalFallback(getIsInboundExternal: (iciId: number) => boolean, getIsFallback: (iciId: number) => boolean) {
      return (iciId: number) => {
         return getIsInboundExternal(iciId) && getIsFallback(iciId);
      };
   }

   // ICI_INBOUND + ICI_EXTERNAL + ICI_FALLBACK + ICI_BUSY
   @Selector([IciHelperSelector.getIsInboundExternalFallback, IciHelperSelector.getIsBusy])
   public static getIsInboundExternalFallbackBusy(getIsInboundExternalFallback: (iciId: number) => boolean, getIsBusy: (iciId: number) => boolean) {
      return (iciId: number) => {
         return getIsInboundExternalFallback(iciId) && getIsBusy(iciId);
      };
   }

   // ICI_INBOUND + ICI_EXTERNAL + ICI_FALLBACK + ICI_NO_ANSWER
   @Selector([IciHelperSelector.getIsInboundExternalFallback, IciHelperSelector.getIsNoAnswer])
   public static getIsInboundExternalFallbackIsNoAnswer(getIsInboundExternalFallback: (iciId: number) => boolean, getIsNoAnswer: (iciId: number) => boolean) {
      return (iciId: number) => {
         return getIsInboundExternalFallback(iciId) && getIsNoAnswer(iciId);
      };
   }

   // ICI_INBOUND + ICI_EXTERNAL + ICI_FALLBACK + ICI_FORWARDED
   @Selector([IciHelperSelector.getIsInboundExternalFallback, IciHelperSelector.getIsForwarded])
   public static getIsInboundExternalFallbackIsForwarded(getIsInboundExternalFallback: (iciId: number) => boolean, getIsForwarded: (iciId: number) => boolean) {
      return (iciId: number) => {
         return getIsInboundExternalFallback(iciId) && getIsForwarded(iciId);
      };
   }
}
